/**
 * Fleet — renderização INSTANCED de todos os avatares (carros e pessoas).
 *
 * Em vez de um Group com ~7–12 meshes por jogador (centenas de draw calls com
 * 100+ jogadores), cada "peça" do avatar vira um único InstancedMesh que
 * desenha TODAS as instâncias daquele tipo em uma só chamada. Resultado:
 * chamadas por peça/variante ativa, sem multiplicá-las pelo número de jogadores.
 *
 * As cores que variam por jogador (pintura do carro, camisa/pele) usam
 * instanceColor; a animação (rodas girando, pernas/braços balançando) é feita
 * recompondo a matriz local das peças animadas a cada frame.
 *
 * LINHAS COMPACTAS POR PEÇA: o catálogo tem 40 peças, mas só 9–17 aparecem numa
 * dada aparência (as variantes de cabelo/roupa/pé que ele não usa ficam de fora).
 * Antes TODA peça desenhava TODOS os avatares e as não-combinantes recebiam uma
 * matriz de escala 0 — não viravam pixel, mas passavam inteiras pelo vertex
 * shader: 67% do trabalho de vértice era jogado fora (26.840 tris/avatar contra
 * 8.791 realmente visíveis). Agora cada peça tem a SUA lista densa: só os
 * avatares que de fato usam a peça ocupam uma instância nela, e `mesh.count` é o
 * tamanho dessa lista. `rowOf[slot]` dá a linha do avatar naquela peça (-1 = não
 * usa) e `slotAt[row]` faz o caminho de volta (necessário no swap-remove).
 * A aparência de um avatar só muda via release+acquire (ver EntityManager._swapKind),
 * então a filiação peça↔avatar é fixa entre esses dois pontos.
 */
import * as THREE from 'three';
import { buildPersonParts } from './personModel.js';
import { buildCarParts } from './carModel.js';
import { partVisible, partColor } from './partsRuntime.js';
import { probes, P } from '../perfProbes.js';
import { createPersonPose, evaluatePersonPose, personPartMatrix } from './person/pose.js';

const CAP = 2048; // capacidade máxima de instâncias por tipo
// Entradas que determinam TODAS as matrizes de um avatar num quadro. Se nenhuma
// mudou, as matrizes seriam recalculadas bit a bit iguais — ver `write`.
// [x, y efetivo, z, heading, escala, squash, swing, waveZ, waveZL, wheelSpin, steer, fase, caminhada, sentar, apoio]
export const FLEET_INPUT_COUNT = 15;
const IN_N = FLEET_INPUT_COUNT;
const CAR_SCALE = 1.25;
const PERSON_SCALE = 1.2;
const HALF_PI = Math.PI / 2;

// temporários reaproveitados (evita alocação por frame)
const _world = new THREE.Matrix4();
const _local = new THREE.Matrix4();
const _inst = new THREE.Matrix4();
const _pos = new THREE.Vector3();
const _pos2 = new THREE.Vector3();
const _quat = new THREE.Quaternion();
const _euler = new THREE.Euler();
const _col = new THREE.Color();
const _ONE = new THREE.Vector3(1, 1, 1);
const _personPose = createPersonPose();

export class Fleet {
  constructor(scene) {
    this.root = new THREE.Group();
    this.root.name = 'fleet';
    scene.add(this.root);
    // CARROS em BUCKETS POR MODELO: cada peça só desenha as instâncias do PRÓPRIO
    // modelo. Antes, toda peça de carro desenhava TODOS os carros (não-combinantes
    // em escala 0) → ~5× de matrizes escritas/upload e de vértices na GPU à toa.
    // O slot devolvido é CODIFICADO (base do bucket + slot local) → a API externa
    // (acquire/release/write com kind 'car'|'person') não muda.
    const byModel = new Map();
    for (const d of buildCarParts()) {
      const list = byModel.get(d.option) || [];
      list.push(d);
      byModel.set(d.option, list);
    }
    this.carBuckets = [...byModel.values()].map((parts, i) => {
      const k = this._makeKind(parts, CAR_SCALE);
      k.base = i * CAP; // offset que codifica o bucket dentro do nº do slot
      return k;
    });
    this.carModelIndex = new Map([...byModel.keys()].map((m, i) => [m, i]));
    this.person = this._makeKind(buildPersonParts(), PERSON_SCALE);
    this.person.base = 0;
  }

  /** Resolve o bucket dono do slot (carros: bucket codificado no próprio slot). */
  _kindOf(kind, slot) {
    return kind === 'person' ? this.person : this.carBuckets[(slot / CAP) | 0];
  }

  /** Tire radius in world units, from the model's own wheel descriptors. */
  carWheelRadius(slot) {
    const k = this._kindOf('car', slot);
    return k.scale * k.wheelRadius;
  }

  _makeKind(parts, scale) {
    const compiled = parts.map((d) => {
      let geometry=d.geo, deformation=null;
      if (d.deform) {
        // Geometry wrapper shares the original vertex/index buffers. Only the
        // per-instance bend attribute differs between left/right/variant lots.
        geometry=new THREE.BufferGeometry(); geometry.setIndex(d.geo.index);
        for(const [name,attribute] of Object.entries(d.geo.attributes))geometry.setAttribute(name,attribute);
        deformation=new THREE.InstancedBufferAttribute(new Float32Array(CAP),1).setUsage(THREE.DynamicDrawUsage);
        geometry.setAttribute('personDeform',deformation);
      }
      const mesh = new THREE.InstancedMesh(geometry, d.mat, CAP);
      mesh.count = 0;
      // avatares NÃO entram no shadow map (estático, só cenário) — eles têm
      // "blob shadows" macias e baratas que os seguem (ver BlobShadows)
      mesh.castShadow = false;
      mesh.receiveShadow = false;
      mesh.frustumCulled = false; // instâncias espalhadas pela cidade
      mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      if (d.color) {
        // cria o atributo de cor (branco) — multiplicado pela cor da instância
        mesh.instanceColor = new THREE.InstancedBufferAttribute(
          new Float32Array(CAP * 3).fill(1),
          3
        );
        mesh.instanceColor.setUsage(THREE.DynamicDrawUsage);
      }
      // peças sem animação têm matriz local constante (pré-computada)
      if (!d.anim) {
        const m = new THREE.Matrix4();
        _euler.set(d.rot?.[0] ?? 0, d.rot?.[1] ?? 0, d.rot?.[2] ?? 0);
        _quat.setFromEuler(_euler);
        m.compose(_pos.set(d.pos[0], d.pos[1], d.pos[2]), _quat, _ONE);
        d.localStatic = m;
      }
      this.root.add(mesh);
      // lista DENSA da peça: `count` instâncias ativas; rowOf[slot] = linha do
      // avatar nesta peça (-1 se ele não usa) e slotAt[row] = avatar dono da linha.
      return {
        mesh, def: d, count: 0, deformation, deformLo:-1, deformHi:-1,
        // faixa de linhas sujas neste quadro (-1 = peça limpa). Substitui o booleano
        // `dirty`: agora o flush envia só de `rowLo` até `rowHi` em vez de 0..count.
        rowLo: -1, rowHi: -1,
        colLo: -1, colHi: -1, // idem para o instanceColor
        rowOf: new Int16Array(CAP).fill(-1),
        slotAt: new Int16Array(CAP).fill(-1)
      };
    });
    return {
      parts: compiled,
      wheelRadius: parts.find(p => p.wheelRadius)?.wheelRadius,
      // PEÇAS ANIMADAS (as que têm `def.anim`). Quando a posição/rumo/escala de um
      // avatar não mudam mas os membros/rodas sim, a matriz de mundo é bit a bit a
      // mesma e só estas peças precisam ser recompostas — as estáticas dariam
      // `_world × localStatic` idêntico.
      //
      // MEDIDO (scripts/matrixBench.mjs, 500 avatares): esse caso é 0,0% dos quadros
      // hoje, porque toda postura de avatar parado oscila `postureY`/`renderHeading`
      // junto com os membros, então o mundo muda sempre que a animação muda. A
      // separação fica aqui porque é correta e custa um `if`; passa a valer no dia em
      // que alguma postura deixar o corpo imóvel.
      animParts: compiled.filter((p) => p.def.anim),
      scale, count: 0, slotRef: new Array(CAP).fill(null), base: 0,
      dirty: false, // algo mudou neste bucket? (evita varrer as peças no flush)
      // últimas ENTRADAS escritas por slot (Float64Array: comparar contra um
      // Float32Array daria falso-negativo, porque o valor lido volta arredondado)
      lastIn: new Float64Array(CAP * IN_N),
      hasIn: new Uint8Array(CAP)
    };
  }

  /** Amplia a faixa de linhas de matriz a enviar desta peça. */
  _touchRow(part, row) {
    if (part.rowLo < 0 || row < part.rowLo) part.rowLo = row;
    if (row > part.rowHi) part.rowHi = row;
  }

  /** Amplia a faixa de linhas de COR a enviar desta peça. */
  _touchCol(part, row) {
    if (part.colLo < 0 || row < part.colLo) part.colLo = row;
    if (row > part.colHi) part.colHi = row;
  }

  _touchDeform(part,row) {
    if(part.deformLo<0||row<part.deformLo)part.deformLo=row;
    if(row>part.deformHi)part.deformHi=row;
  }

  /** Reserva um slot de instância para uma entidade. Retorna o índice (codificado). */
  acquire(kind, colors, ref) {
    const k = kind === 'person'
      ? this.person
      : this.carBuckets[this.carModelIndex.get(ref?.appearance?.model) ?? 0];
    if (k.count >= CAP) return -1; // capacidade cheia → não corrompe o buffer (caller adia)
    const slot = k.count++;
    k.slotRef[slot] = ref;
    k.hasIn[slot] = 0; // sem estado anterior → a 1a escrita nunca é pulada
    // reserva uma linha SÓ nas peças que esta aparência realmente mostra
    const app = ref?.appearance;
    for (const part of k.parts) {
      if (!partVisible(part.def, app)) { part.rowOf[slot] = -1; continue; }
      const row = part.count++;
      part.rowOf[slot] = row;
      part.slotAt[row] = slot;
      part.mesh.count = part.count;
      this._writeColor(part, row, colors);
    }
    k.dirty = true;
    return k.base + slot;
  }

  /**
   * Libera o slot. Duas remoções por troca (swap-remove) em cascata:
   *   1) tira o avatar da lista densa de CADA peça que ele usava;
   *   2) tira o avatar do bucket, trazendo o último avatar para o slot vago —
   *      e o mapa rowOf/slotAt das peças acompanha a mudança de slot.
   */
  release(kind, slot) {
    if (slot < 0) return; // acquire falhou (CAP cheio): não há nada a liberar — e
    // _kindOf com slot -1 indexaria o bucket errado, corrompendo contadores
    const k = this._kindOf(kind, slot);
    const local = slot - k.base;
    const last = k.count - 1;

    // 1) remove das peças. COPIA matriz e cor do último p/ a linha vaga AGORA:
    // sem isto, se o avatar movido já tiver sido escrito neste frame (ex.: uma
    // transformação carro↔pessoa no meio do loop), ele fica 1 frame com os dados
    // antigos daquela linha → "pisca"/fantasma.
    for (const part of k.parts) {
      const row = part.rowOf[local];
      if (row < 0) continue;
      const lastRow = part.count - 1;
      if (row !== lastRow) {
        const owner = part.slotAt[lastRow];
        const a = part.mesh.instanceMatrix;
        a.array.copyWithin(row * 16, lastRow * 16, lastRow * 16 + 16);
        this._touchRow(part, row); // a troca mexeu no buffer, mesmo sem passar pelo write
        const c = part.mesh.instanceColor;
        if (c) {
          c.array.copyWithin(row * 3, lastRow * 3, lastRow * 3 + 3);
          this._touchCol(part, row);
        }
        if (part.deformation) {
          part.deformation.array[row]=part.deformation.array[lastRow];
          this._touchDeform(part,row);
        }
        part.rowOf[owner] = row;
        part.slotAt[row] = owner;
      }
      part.rowOf[local] = -1;
      part.slotAt[lastRow] = -1;
      part.count = lastRow;
      part.mesh.count = lastRow;
    }

    // 2) remove do bucket: o ÚLTIMO avatar assume o slot liberado
    if (local !== last && last >= 0) {
      const moved = k.slotRef[last];
      moved.slot = k.base + local;
      k.slotRef[local] = moved;
      for (const part of k.parts) {
        const r = part.rowOf[last];
        part.rowOf[local] = r;
        part.rowOf[last] = -1;
        if (r >= 0) part.slotAt[r] = local;
      }
    }
    k.slotRef[last] = null;
    k.hasIn[local] = 0; // trocou de dono (ou esvaziou) → força reescrever
    k.hasIn[last] = 0;
    k.dirty = true;
    k.count = Math.max(0, last);
  }

  /** Recalcula e grava as matrizes de cada peça da instância `slot`. */
  write(kind, slot, x, y, z, heading, anim) {
    if (slot < 0) return; // entidade sem slot (CAP cheio no acquire) → nada a escrever
    probes.begin(P.fleetWrite);
    const k = this._kindOf(kind, slot);
    const s = slot - k.base;
    const sm = k.scale * (anim.scaleMul ?? 1); // escala da transformação
    const isPerson=kind==='person';
    const sy = isPerson?1:(anim.squashY ?? 1);
    if (isPerson) {evaluatePersonPose(anim,_personPose);y+=sm*_personPose.drop;}

    // Cache exact inputs, including the body's rig offset and deformation phase.
    // Equal world inputs allow static pieces to keep their existing matrices.
    const sw = anim.swing ?? 0, wz = anim.waveZ ?? 0, wl = anim.waveZL ?? 0;
    const ws = anim.wheelSpin ?? 0, st = anim.steer ?? 0;
    const phase=anim.phase??0,gait=anim.gait??0,sit=anim.sit??0,settle=anim.settle??0;
    // The first six entries define world placement; the remaining nine define
    // limb matrices and per-instance deformation, including changes with no travel.
    const L = k.lastIn, o = s * IN_N;
    const visto = k.hasIn[s] === 1;
    const mundoIgual = visto &&
      L[o] === x && L[o + 1] === y && L[o + 2] === z && L[o + 3] === heading &&
      L[o + 4] === sm && L[o + 5] === sy;
    if (mundoIgual &&
        L[o + 6] === sw && L[o + 7] === wz && L[o + 8] === wl &&
        L[o + 9] === ws && L[o + 10] === st && L[o+11]===phase && L[o+12]===gait && L[o+13]===sit && L[o+14]===settle) { probes.end(P.fleetWrite); return; }
    L[o] = x; L[o + 1] = y; L[o + 2] = z; L[o + 3] = heading;
    L[o + 4] = sm; L[o + 5] = sy; L[o + 6] = sw; L[o + 7] = wz;
    L[o + 8] = wl; L[o + 9] = ws; L[o + 10] = st;
    L[o+11]=phase;L[o+12]=gait;L[o+13]=sit;L[o+14]=settle;
    k.hasIn[s] = 1;
    k.dirty = true;
    // mundo idêntico → `_world` sai bit a bit igual, então `_world × localStatic` das
    // peças ESTÁTICAS não muda: basta refazer as animadas.
    const lista = mundoIgual ? k.animParts : k.parts;
    const scXZ = sm / Math.sqrt(sy);           // largura compensa (volume ~ constante)
    _quat.setFromEuler(_euler.set(0, heading, 0));
    _world.compose(_pos.set(x, y, z), _quat, _pos2.set(scXZ, sm * sy, scXZ));
    for (const part of lista) {
      // peça que esta aparência não mostra: não ocupa instância nesta peça
      // (a filiação foi decidida no acquire, por partVisible) → nada a escrever.
      const row = part.rowOf[s];
      if (row < 0) continue;
      const { mesh, def } = part;
      let local = def.localStatic;
      if (isPerson && def.anim) {
        const bend=personPartMatrix(def,_personPose,_local);
        if (part.deformation && part.deformation.getX(row)!==Math.fround(bend)) {
          part.deformation.setX(row,bend);this._touchDeform(part,row);
        }
        local=_local;
      } else if (!local) {
        if (def.anim === 'footL' || def.anim === 'footR') {
          // o pé/calçado acompanha a perna: gira em torno do quadril (hipY)
          const s = def.anim === 'footL' ? anim.swing : -anim.swing;
          _quat.setFromEuler(_euler.set(s, 0, 0));
          _pos.set(0, def.pos[1] - def.hipY, def.pos[2]).applyQuaternion(_quat);
          _pos.x += def.pos[0];
          _pos.y += def.hipY;
          _local.compose(_pos, _quat, _ONE);
        } else {
          const r = def.rot;
          let ex = r ? r[0] : 0;
          let ey = r ? r[1] : 0;
          const ez = r ? r[2] : 0;
          let ezv = ez;
          switch (def.anim) {
            case 'wheel': ex += ws; break;
            // Local X rolls inside the steering rotation around Y.
            case 'wheelF': ex += ws; ey += st; break;
            case 'legL': ex = anim.swing; break;
            case 'legR': ex = -anim.swing; break;
            case 'armL':
              // `waveZL` ergue o braço ESQUERDO (espelhado do direito) — permite
              // posturas com os DOIS braços (celebrate/dance/spin/talk alternado)
              if (anim.waveZL) ezv = -anim.waveZL;
              else ex = -anim.swing * 0.8;
              break;
            case 'armR':
              // aceno: braço direito ergue (gira em Z) e oscila; senão, balança normal
              if (anim.waveZ) ezv = anim.waveZ;
              else ex = anim.swing * 0.8;
              break;
          }
          // roda dianteira: ordem YXZ (esterço Y por fora, rolagem X por dentro);
          // demais peças mantêm a ordem padrão.
          if (def.anim === 'wheelF') _euler.set(ex, ey, ezv, 'YXZ');
          else _euler.set(ex, ey, ezv);
          _quat.setFromEuler(_euler);
          _local.compose(_pos.set(def.pos[0], def.pos[1], def.pos[2]), _quat, _ONE);
        }
        local = _local;
      }
      _inst.multiplyMatrices(_world, local);
      mesh.setMatrixAt(row, _inst);
      this._touchRow(part, row);
    }
    probes.end(P.fleetWrite);
  }

  /** Sobe as matrizes alteradas para a GPU (apenas a faixa ativa de cada peça). */
  flush() {
    probes.begin(P.fleetFlush);
    for (const k of this.carBuckets) this._flushKind(k);
    this._flushKind(this.person);
    probes.end(P.fleetFlush);
  }

  /**
   * Sobe à GPU só as FAIXAS que mudaram, e só nos buckets que mudaram.
   *
   * Pressupõe uma chamada de flush por quadro renderizado — que é o contrato do
   * EntityManager.update(), chamado pelo updater do SceneManager, que por sua vez só
   * roda em quadros que renderizam. Se o flush rodasse duas vezes entre dois renders,
   * a segunda faixa apagaria a primeira e sobrariam linhas velhas na GPU.
   */
  _flushKind(k) {
    if (!k.count || !k.dirty) return; // bucket vazio ou intocado → nem varre as peças
    k.dirty = false;
    for (const part of k.parts) {
      if(part.deformLo>=0) {
        part.deformation.clearUpdateRanges();
        part.deformation.addUpdateRange(part.deformLo,part.deformHi-part.deformLo+1);
        part.deformation.needsUpdate=true;
        part.deformLo=part.deformHi=-1;
      }
      if (part.rowLo >= 0) {
        const a = part.mesh.instanceMatrix;
        a.clearUpdateRanges();
        a.addUpdateRange(part.rowLo * 16, (part.rowHi - part.rowLo + 1) * 16);
        a.needsUpdate = true;
        part.rowLo = part.rowHi = -1;
      }
      // COR: só muda quando alguém entra, troca de visual (restyle → release+acquire)
      // ou o swap-remove desloca uma linha. Antes cada uma dessas escritas marcava o
      // atributo inteiro como sujo — e sem faixa o three reenvia o array TODO
      // (CAP × 3 floats por peça). Medido: a entrada dos 500 avatares custava 51 MB de
      // upload de cor, contra um piso de ~50 KB.
      if (part.colLo >= 0) {
        const c = part.mesh.instanceColor;
        c.clearUpdateRanges();
        c.addUpdateRange(part.colLo * 3, (part.colHi - part.colLo + 1) * 3);
        c.needsUpdate = true;
        part.colLo = part.colHi = -1;
      }
    }
  }

  /** Grava a cor da instância `row` de uma peça (se ela tinge por jogador). */
  _writeColor(part, row, colors) {
    const { mesh, def } = part;
    if (!def.color) return;
    _col.set(partColor(def, colors)); // mesma resolução de cor do /models (partsRuntime)
    mesh.setColorAt(row, _col);
    this._touchCol(part, row); // o upload sai no flush, na faixa (ver _flushKind)
  }
}
